'use client';

import Link from 'next/link';
import CountryFAQ from '@/components/country/CountryFAQ';
import GfSectionMark from '@/components/common/GfSectionMark';
import GfArticleCards from '@/components/common/GfArticleCards';
import GfSubnav from '@/components/common/GfSubnav';
import GfVerticalHero from '@/components/common/GfVerticalHero';
import GfSteps from '@/components/common/GfSteps';
import GfCta from '@/components/common/GfCta';
import GfProviderCard from '@/components/common/GfProviderCard';
import AffiliateLink from '@/components/common/AffiliateLink';
import GfPromoCodes from '@/components/country/GfPromoCodes';
import { ICONS, EMOJI_ICON } from '@/components/common/gfIcons';
import { renderTablerIcon } from '@/utils/iconRenderer';
import { makeCountryDisplay } from '@/lib/content/declension';
import { t } from '@/i18n/translations';

// Card icons may arrive as a Tabler name ("TbAmbulance"), an emoji ("🏥") or
// nothing. Resolve a real, content-matched glyph; fall back per section so a
// missing/unknown icon never makes every card look identical.
const isTablerName = (n) => typeof n === 'string' && /^Tb[A-Z]/.test(n);
const resolveCardIcon = (name, fallback) =>
    (isTablerName(name) ? renderTablerIcon(name, { size: 26, className: 'ri' }) : null)
    || ICONS[EMOJI_ICON[name]]
    || fallback;

export default function InsurancePageContent({ content, lang = 'ru', insuranceArticles = [], promotions = [], background, country, countryName, localNotes = null }) {
    const isRu = lang === 'ru';
    const { cName, cCase } = makeCountryDisplay(countryName, country, isRu);
    const sp = t(lang, 'servicePage');        // общие тексты сервис-страниц
    const ip = t(lang, 'insurancePage');      // специфика страховки

    const whyCards = Array.isArray(content.whyCards) ? content.whyCards : [];
    const benefits = Array.isArray(content.benefits) ? content.benefits : [];
    const instructions = Array.isArray(content.instructions) ? content.instructions : [];
    const providers = Array.isArray(content.providers) ? content.providers : [];
    const faqItems = (Array.isArray(content.faq) ? content.faq : []).map((it) => ({ question: it.q ?? it.question, answer: it.a ?? it.answer }));

    const whyTitle = content.whyTitle || ip.whyTitle;
    const benefitsTitle = content.benefitsTitle || ip.benefitsTitle;
    const instructionTitle = content.instructionTitle || ip.instructionTitle;

    const has = {
        why: whyCards.length > 0,
        benefits: benefits.length > 0,
        local: Boolean(localNotes),
        providers: providers.length > 0,
        how: instructions.length > 0,
        promos: promotions?.length > 0,
        faq: faqItems.length > 0,
        articles: insuranceArticles?.length > 0,
    };

    const anchors = [
        (has.why || has.benefits) && { href: '#features', label: ip.aboutAnchor },
        has.local && { href: '#local', label: localNotes.eyebrow },
        has.providers && { href: '#providers', label: sp.anchor.providers },
        has.how && { href: '#how', label: sp.anchor.how },
        has.promos && { href: '#promocodes', label: sp.anchor.discounts },
        has.faq && { href: '#faq', label: sp.anchor.faq },
        has.articles && { href: '#articles', label: sp.anchor.articles },
    ].filter(Boolean);

    return (
        <div className="gf-root">
            <GfVerticalHero background={background} title={content.title} sub={content.description}>
                {has.providers && <a className="pri" href="#providers">{ip.heroChoose} <b>→</b></a>}
                {has.how && <a className="sec" href="#how">{ip.heroHow}</a>}
            </GfVerticalHero>

            <GfSubnav lang={lang} leaf={ip.leaf} anchors={anchors}
                crumbs={[cName && { href: `/${lang}/${country}`, label: cName }]} />

            {has.why && (
                <section className="gf-sec" id="features">
                    <GfSectionMark eyebrow={ip.whyEyebrow} title={whyTitle} />
                    {content.whySubtitle && <p className="gf-lead">{content.whySubtitle}</p>}
                    <div className="gf-scen gf-scen-feat cols-2">
                        {whyCards.map((card, i) => (
                            <article className="sc" key={i}>
                                <div className="ri-row">{resolveCardIcon(card.icon, ICONS.shieldPlus)}<h3>{card.title}</h3></div>
                                {card.subtitle && <p>{card.subtitle}</p>}
                            </article>
                        ))}
                    </div>
                </section>
            )}

            {has.benefits && (
                <section className="gf-sec" style={{ paddingTop: has.why ? 0 : undefined }}>
                    <GfSectionMark eyebrow={sp.eyebrow.benefits} title={benefitsTitle} />
                    {content.benefitsSubtitle && <p className="gf-lead">{content.benefitsSubtitle}</p>}
                    <div className="gf-scen gf-scen-feat cols-2">
                        {benefits.map((b, i) => (
                            <article className="sc" key={i}>
                                <div className="ri-row">{resolveCardIcon(b.icon, ICONS.check)}<h3>{b.title}</h3></div>
                                {b.desc && <p>{b.desc}</p>}
                            </article>
                        ))}
                    </div>
                </section>
            )}

            {has.local && (
                <section className="gf-sec gf-localnet" id="local" style={{ paddingTop: (has.why || has.benefits) ? 0 : undefined }}>
                    <GfSectionMark eyebrow={localNotes.eyebrow} tag={cName || ''} title={localNotes.heading} />
                    {localNotes.intro && <p className="gf-lead">{localNotes.intro}</p>}
                    {localNotes.points.length > 0 && (
                        <ul className="gf-localnet-list">
                            {localNotes.points.map((point, i) => (
                                <li key={i}>{point}</li>
                            ))}
                        </ul>
                    )}
                    {localNotes.pricing && <p className="gf-localnet-pricing">{localNotes.pricing}</p>}
                    {localNotes.outro && <p className="gf-localnet-outro">{localNotes.outro}</p>}
                </section>
            )}

            {has.providers && (
                <section className="gf-sec" id="providers" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.providers} tag={providers.length} title={ip.providersTitle} />
                    {content.providersSubtitle && <p className="gf-lead">{content.providersSubtitle}</p>}
                    <div className="gf-grid gf-grid-center">
                        {providers.map((p) => (
                            <GfProviderCard key={p.slug} name={p.name} logo={p.logo} description={p.description} className="pc-whole">
                                <div className="gf-prov-actions">
                                    {p.site && (
                                        <AffiliateLink href={p.site} provider={p.slug} vertical="insurance" country={country} position="listing_card" className="buy">
                                            {sp.toSite}
                                        </AffiliateLink>
                                    )}
                                    <Link className="visit" href={`/${lang}/${country}/insurance/${p.slug}`}>{sp.details}</Link>
                                </div>
                            </GfProviderCard>
                        ))}
                    </div>
                </section>
            )}

            {has.how && (
                <section className="gf-sec" id="how" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.guide} tag={`${instructions.length} ${sp.stepsWord(instructions.length)}`} title={instructionTitle} />
                    {content.instructionSubtitle && <p className="gf-lead">{content.instructionSubtitle}</p>}
                    <GfSteps steps={instructions} />
                </section>
            )}

            {has.promos && (
                <section className="gf-sec" id="promocodes" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.discounts} tag={sp.tag.current} title={ip.promosTitle} />
                    <p className="gf-lead">{ip.promoLead}</p>
                    <GfPromoCodes promotions={promotions} lang={lang} country={country} />
                </section>
            )}

            {has.faq && (
                <section className="gf-sec" id="faq" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.qa} tag={sp.tag.faq} title={sp.popularQuestions} />
                    {content.faqSubtitle && <p className="gf-lead">{content.faqSubtitle}</p>}
                    <CountryFAQ faqItems={faqItems} lang={lang} variant="gf" />
                    {content.faqLink?.href && content.faqLink?.text && (
                        <div style={{ marginTop: 26 }}><Link className="gf-alllink" href={content.faqLink.href}>{content.faqLink.text}</Link></div>
                    )}
                </section>
            )}

            {has.articles && (
                <section className="gf-sec" id="articles" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.resources} tag={sp.tag.blog} title={ip.articlesTitle} allHref={`/${lang}/blog/category/insurance`} allLabel={sp.allArticles} />
                    <p className="gf-lead">{ip.articlesLead}</p>
                    <GfArticleCards posts={insuranceArticles} lang={lang} variant="bento" bucket />
                </section>
            )}

            <GfCta
                eyebrow={ip.ctaEyebrow}
                title={ip.ctaTitle(cName ? (isRu ? cCase('accusative') : cName) : '')}
            >
                {has.providers && <a className="gf-btn" href="#providers">{ip.ctaBtn}</a>}
            </GfCta>
        </div>
    );
}
